
import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Card,
  CardHeader,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Badge,
  VStack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { projectsAPI, timesheetAPI } from '../services/api';

const ManagerTimesheetApproval: React.FC = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState<any[]>([]);
  const [timesheets, setTimesheets] = useState<any[]>([]);
  const [associates, setAssociates] = useState<any[]>([]);
  const toast = useToast();

  useEffect(() => {
    if (user && user._id) {
      projectsAPI.getAll().then(res => {
        // Only projects managed by this manager
        const managed = res.data.filter((p: any) => p.managerId === user._id);
        setProjects(managed);
      });
      timesheetAPI.getManagerTimesheets(user._id).then(res => setTimesheets(res.data));
      import('../services/api').then(m => m.associateAPI.getAllAssociates()).then(resA => setAssociates(resA.data));
    }
  }, [user]);

  const handleStatusChange = async (id: number, status: 'approved' | 'rejected') => {
    try {
      await timesheetAPI.updateTimesheetStatus(id, status);
      setTimesheets(prev => prev.map(ts => ts._id === id ? { ...ts, status: status.toUpperCase() } : ts));
      toast({
        title: `Timesheet ${status}`,
        description: `Timesheet has been ${status}.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Failed to update status.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };
// Format week range as 'YYYY-MM-DD to YYYY-MM-DD' or 'MM-DD to MM-DD' if year omitted
function formatWeek(start: string, end: string): string {
  if (!start || !end) return '-';
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return `${start} to ${end}`;
  // Format as DD-MM to DD-MM
  const format = (date: Date) => `${date.getDate().toString().padStart(2,'0')}-${(date.getMonth()+1).toString().padStart(2,'0')}`;
  return `${format(startDate)} to ${format(endDate)}`;
}
  // Helper for badge color
  const getStatusColor = (status: string) => {
    if (status === 'PENDING') return 'yellow';
    if (status === 'APPROVED') return 'green';
    if (status === 'REJECTED') return 'red';
    return 'gray';
  };

  return (
    <Box p={8}>
      <Heading size="lg" mb={6}>Manager Timesheet Approval</Heading>
      <Card>
        <CardHeader>
          <Heading size="md">Timesheets for Your Projects</Heading>
        </CardHeader>
        <CardBody>
          <Table variant="striped" colorScheme="gray" size="md" boxShadow="md" borderRadius="md">
            <Thead>
              <Tr>
                <Th py={3} px={2} textAlign="center">Timesheet ID</Th>
                <Th py={3} px={2} textAlign="center">Project</Th>
                <Th py={3} px={2} textAlign="center">Associate</Th>
                <Th py={3} px={2} textAlign="center" minWidth="140px" maxWidth="180px">Week</Th>
                <Th py={3} px={2} textAlign="center">Total Hours</Th>
                <Th py={3} px={2} textAlign="center">Status</Th>
                <Th py={3} px={2} textAlign="center">Note</Th>
                <Th py={3} px={2} textAlign="center">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {timesheets.length === 0 ? (
                <Tr><Td colSpan={8} py={6} textAlign="center"><Text>No timesheets found for your projects.</Text></Td></Tr>
              ) : (
                timesheets.map((ts: any) => {
                  const project = projects.find((p: any) => String(p._id) === String(ts.projectId));
                  const associate = associates.find((a: any) => a._id === ts.associateId);
                  return (
                    <Tr key={ts._id}>
                      <Td py={3} px={2} textAlign="center" fontWeight="medium">{ts._id}</Td>
                      <Td py={3} px={2} textAlign="center">{project ? project.name : ts.projectId}</Td>
                      <Td py={3} px={2} textAlign="center">{associate ? associate.name : ts.associateId}</Td>
                      <Td py={3} px={2} textAlign="center" fontFamily="monospace" fontSize="md" minWidth="140px" maxWidth="180px" whiteSpace="nowrap">{formatWeek(ts.weekStart, ts.weekEnd)}</Td>
                      <Td py={3} px={2} textAlign="center">{ts.totalHours}</Td>
                      <Td py={3} px={2} textAlign="center">
                        <Badge colorScheme={getStatusColor(ts.status?.toUpperCase())} variant="solid" px={4} py={2} fontSize="md" borderRadius="md">
                          {ts.status?.toUpperCase() || 'PENDING'}
                        </Badge>
                      </Td>
                      <Td py={3} px={2} textAlign="center" whiteSpace="pre-line">{ts.note || '-'}</Td>
                      <Td py={3} px={2} textAlign="center">
                        {ts.status === 'pending' && (
                          <VStack spacing={2} align="center">
                            <Button colorScheme="green" size="sm" onClick={() => handleStatusChange(ts._id, 'approved')}>Approve</Button>
                            <Button colorScheme="red" size="sm" onClick={() => handleStatusChange(ts._id, 'rejected')}>Reject</Button>
                          </VStack>
                        )}
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
        </CardBody>
      </Card>
    </Box>
  );
};

export default ManagerTimesheetApproval;
